// Fernando Hernandez 03.Jul.2026
// WD-1184: Autodetección de zona horaria y moneda para preselecciones.
// Resuelve la zona horaria del navegador (con fallback por IP) contra el
// catálogo de zonas y deriva la moneda según el país de la zona detectada.
// Usado por TimezoneDropdownInput/CurrencyDropdownInput (autollenado a nivel
// componente solo cuando el campo está vacío) y por el crear usuario express.

interface TimeZoneCatalogItem {
  zoneId: number | string;
  zoneName: string;
  countryCode?: string;
}

interface CurrencyCatalogItem {
  currencyId: number;
  countryCode: string;
}

// Zona horaria IANA del navegador (ej. 'America/Guatemala') o null si no se puede resolver
export const detectBrowserTimeZoneName = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    return null;
  }
};

// Fallback por IP usando ipapi.co (sin API key); devuelve el nombre IANA o null
export const detectTimeZoneNameByIp = async (): Promise<string | null> => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return typeof data?.timezone === 'string' ? data.timezone : null;
  } catch {
    return null;
  }
};

// Busca en el catálogo la zona detectada (navegador primero, luego IP)
export const resolveDefaultZone = async (
  timeZones: TimeZoneCatalogItem[]
): Promise<TimeZoneCatalogItem | null> => {
  const matchZone = (zoneName: string | null) =>
    zoneName ? timeZones.find((tz) => tz.zoneName === zoneName) ?? null : null;

  const browserZone = matchZone(detectBrowserTimeZoneName());
  if (browserZone) {
    return browserZone;
  }
  return matchZone(await detectTimeZoneNameByIp());
};

// Moneda según el país de la zona detectada. Los CountryCode difieren en
// mayúsculas/minúsculas entre Zones y Currencies, por eso el match es case-insensitive
export const resolveDefaultCurrencyId = (
  zone: TimeZoneCatalogItem | null,
  currencies: CurrencyCatalogItem[]
): number | null => {
  const countryCode = zone?.countryCode?.toUpperCase();
  if (!countryCode) {
    return null;
  }
  return (
    currencies.find((currency) => currency.countryCode?.toUpperCase() === countryCode)
      ?.currencyId ?? null
  );
};
